import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    LinearProgress,
    Paper,
    Tooltip,
    Typography
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { useNavigate } from "react-router-dom";

import { myApi } from "../../config/axiosConfig";

const TANKS = [
    {
        id: "colombia-diesel",
        location: "COLOMBIA",
        product: "DIESEL",
        fuelType: "TODOS",
        label: "Diésel",
        unit: "L",
        detailUrl: "/diesel/compras"
    },
    {
        id: "monterrey-diesel",
        location: "MONTERREY",
        product: "DIESEL",
        fuelType: "TODOS",
        label: "Diésel",
        unit: "L",
        detailUrl: "/diesel/compras"
    },
    {
        id: "arguindegui-red",
        location: "ARGUINDEGUI",
        product: "DIESEL",
        fuelType: "RED",
        label: "Diésel rojo",
        unit: "GAL",
        detailUrl: "/diesel/compras"
    },
    {
        id: "arguindegui-clear",
        location: "ARGUINDEGUI",
        product: "DIESEL",
        fuelType: "CLEAR",
        label: "Diésel claro",
        unit: "GAL",
        detailUrl: "/diesel/compras"
    },
    {
        id: "colombia-def",
        location: "COLOMBIA",
        product: "DEF",
        label: "DEF",
        unit: "L",
        detailUrl: "/diesel/compras/def"
    },
    {
        id: "arguindegui-def",
        location: "ARGUINDEGUI",
        product: "DEF",
        label: "DEF",
        unit: "GAL",
        detailUrl: "/diesel/compras/def"
    }
];

const TankGraphic = ({ level = 0, capacity = 0, unit = "L" }) => {
    const safeLevel = Math.max(Number(level) || 0, 0);
    const safeCapacity = Math.max(Number(capacity) || 0, 0);

    const percentage = safeCapacity
        ? Math.min((safeLevel / safeCapacity) * 100, 100)
        : 0;

    const getColor = () => {
        if (percentage >= 95) {
            return "linear-gradient(90deg, #8b0000, #ff2b2b, #8b0000)";
        }

        if (percentage >= 90) {
            return "linear-gradient(90deg, #b45309, #f97316, #b45309)";
        }

        if (percentage >= 80) {
            return "linear-gradient(90deg, #a16207, #facc15, #a16207)";
        }

        return "linear-gradient(90deg, #1f6b16, #35a12a, #1f6b16)";
    };

    return (
        <Box sx={{ textAlign: "center", my: 2 }}>
            <Box
                sx={{
                    width: 92,
                    height: 130,
                    mx: "auto",
                    position: "relative",
                    overflow: "hidden",
                    border: "2px solid #9fb3bd",
                    borderRadius: "52px / 18px",
                    background:
                        "linear-gradient(90deg, #dce8ee, #f7fbfd, #b9cbd4)",
                    boxShadow: "inset -12px 0 20px rgba(0,0,0,.15)"
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        bottom: 0,
                        width: "100%",
                        height: `${percentage}%`,
                        background: getColor(),
                        transition: "height .4s ease"
                    }}
                />

                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                >
                    <Typography
                        fontWeight={800}
                        fontSize={14}
                        color="white"
                        sx={{ textShadow: "0 1px 3px black" }}
                    >
                        {safeLevel.toLocaleString()} {unit}
                    </Typography>
                </Box>
            </Box>

            <Typography fontWeight={800} mt={1}>
                {percentage.toFixed(1)}%
            </Typography>
        </Box>
    );
};

const fetchTank = async (tank) => {
    if (tank.product === "DEF") {
        const [levelResponse, capacityResponse] = await Promise.all([
            myApi.post("/cargasFuel/getDefTankLevel", {
                location: tank.location
            }),
            myApi.post("/cargasFuel/getDefTankCapacity", {
                location: tank.location
            })
        ]);

        return {
            ...tank,
            level: Number(levelResponse.data?.level) || 0,
            capacity: Number(capacityResponse.data?.capacity) || 0
        };
    }

    const [levelResponse, capacityResponse] = await Promise.all([
        myApi.post("/cargasFuel/getTankLevel", {
            location: tank.location,
            fuelType: tank.fuelType
        }),
        myApi.post("/cargasFuel/getTankCapacity", {
            location: tank.location,
            fuelType: tank.fuelType
        })
    ]);

    return {
        ...tank,
        level: Number(levelResponse.data?.level) || 0,
        capacity: Number(capacityResponse.data?.capacity) || 0
    };
};

const getTankStatus = (percentage) => 
{
    if (percentage <= 15) {
        return {
            label: "Nivel crítico",
            color: "#dc2626",
            background: "#fef2f2",
            chipColor: "error"
        };
    }

    if (percentage <= 30) {
        return {
            label: "Nivel bajo",
            color: "#ea580c",
            background: "#fff7ed",
            chipColor: "warning"
        };
    }

    return {
        label: "Nivel normal",
        color: "#16a34a",
        background: "#f0fdf4",
        chipColor: "success"
    };
};

const TankDashboard = () => {
    const navigate = useNavigate();

    const [tanks, setTanks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [generalError, setGeneralError] = useState("");

    const loadTanks = useCallback(async () => {
        setLoading(true);
        setGeneralError("");

        try {
            const results = await Promise.allSettled(
                TANKS.map(fetchTank)
            );

            const tankResults = results.map((result, index) => {
                if (result.status === "fulfilled") {
                    return {
                        ...result.value,
                        error: false
                    };
                }

                return {
                    ...TANKS[index],
                    level: 0,
                    capacity: 0,
                    error: true
                };
            });

            setTanks(tankResults);
            setLastUpdated(new Date());

            if (tankResults.every((tank) => tank.error)) {
                setGeneralError(
                    "No se pudo consultar la información de los tanques."
                );
            }
        } catch (error) {
            console.error("Error dashboard de tanques:", error);
            setGeneralError(
                "Ocurrió un error al consultar los tanques."
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadTanks();
    }, [loadTanks]);

    const openDetails = (tank) => {
        navigate(tank.detailUrl, {
            state: {
                location: tank.location,
                fuelType: tank.fuelType
            }
        });
    };

    return (
        <Box
            sx={{
                p: { xs: 2, md: 3 },
                width: "100%",
                height: "calc(100vh - 160px)",
                minHeight: 0,
                bgcolor: "#f8fafc",
                boxSizing: "border-box",
                overflow: "auto"
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2.5, md: 3 },
                    mb: 3,
                    borderRadius: 4,
                    color: "white",
                    background:
                        "linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)",
                    position: "relative",
                    overflow: "hidden"
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        width: 240,
                        height: 240,
                        borderRadius: "50%",
                        background: "rgba(255,255,255,.08)",
                        top: -120,
                        right: -60
                    }}
                />

                <Box
                    sx={{
                        position: "relative",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: { xs: "flex-start", sm: "center" },
                        flexDirection: { xs: "column", sm: "row" },
                        gap: 2
                    }}
                >
                    <Box>
                        <Typography
                            variant="h4"
                            fontWeight={800}
                            sx={{ letterSpacing: "-0.5px" }}
                        >
                            Dashboard de Tanques
                        </Typography>

                        <Typography sx={{ opacity: 0.85, mt: 0.5 }}>
                            Inventario actual de Diésel y DEF por ubicación
                        </Typography>

                        {lastUpdated && (
                            <Typography
                                variant="caption"
                                sx={{
                                    display: "block",
                                    mt: 1,
                                    opacity: 0.7
                                }}
                            >
                                Actualizado: {lastUpdated.toLocaleString()}
                            </Typography>
                        )}
                    </Box>

                    <Tooltip title="Actualizar niveles">
                        <span>
                            <Button
                                variant="contained"
                                startIcon={
                                    loading
                                        ? <CircularProgress size={18} color="inherit" />
                                        : <RefreshIcon />
                                }
                                onClick={loadTanks}
                                disabled={loading}
                                sx={{
                                    bgcolor: "white",
                                    color: "#1d4ed8",
                                    fontWeight: 700,
                                    borderRadius: 2.5,
                                    px: 2.5,
                                    "&:hover": {
                                        bgcolor: "#eff6ff"
                                    }
                                }}
                            >
                                Actualizar
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </Paper>

            {generalError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {generalError}
                </Alert>
            )}

            {loading && tanks.length === 0 ? (
                <Box
                    sx={{
                        minHeight: 300,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <Grid
                    container
                    spacing={2}
                    sx={{
                        pb: 1
                    }}
                >
                    {tanks.map((tank) => {
                        const safeLevel = Math.max(Number(tank.level) || 0, 0);
                        const safeCapacity = Math.max(Number(tank.capacity) || 0, 0);

                        const available = Math.max(
                            safeCapacity - safeLevel,
                            0
                        );

                        const percentage = safeCapacity
                            ? Math.min((safeLevel / safeCapacity) * 100, 100)
                            : 0;
                        
                        const status = getTankStatus(percentage);
                        const isDef = tank.product === "DEF";

                        return (
                            <Grid
                                key={tank.id}
                                size={{ xs: 12, sm: 6, lg: 4 }}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        height: "100%",
                                        overflow: "hidden",
                                        borderRadius: 4,
                                        border: "1px solid #e2e8f0",
                                        bgcolor: "white",
                                        transition: "all .25s ease",
                                        "&:hover": {
                                            transform: "translateY(-5px)",
                                            boxShadow: "0 18px 45px rgba(15, 23, 42, .12)",
                                            borderColor: "#bfdbfe"
                                        }
                                    }}
                                >
                                    {/* Encabezado */}
                                    <Box
                                        sx={{
                                            p: 2.5,
                                            pb: 2,
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "flex-start"
                                        }}
                                    >
                                        <Box sx={{ display: "flex", gap: 1.5 }}>
                                            <Box
                                                sx={{
                                                    width: 44,
                                                    height: 44,
                                                    borderRadius: 2.5,
                                                    display: "grid",
                                                    placeItems: "center",
                                                    color: isDef ? "#2563eb" : "#0f766e",
                                                    bgcolor: isDef ? "#eff6ff" : "#f0fdfa"
                                                }}
                                            >
                                                {isDef ? (
                                                    <WaterDropOutlinedIcon />
                                                ) : (
                                                    <LocalGasStationIcon />
                                                )}
                                            </Box>

                                            <Box>
                                                <Typography
                                                    variant="h6"
                                                    fontWeight={800}
                                                    lineHeight={1.2}
                                                >
                                                    {tank.location}
                                                </Typography>

                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    mt={0.4}
                                                >
                                                    {tank.label}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {!tank.error && (
                                            <Chip
                                                size="small"
                                                label={status.label}
                                                color={status.chipColor}
                                                variant="outlined"
                                                sx={{ fontWeight: 700 }}
                                            />
                                        )}
                                    </Box>

                                    {tank.error ? (
                                        <Box sx={{ px: 2.5, pb: 2.5 }}>
                                            <Alert severity="error">
                                                No fue posible consultar este tanque.
                                            </Alert>
                                        </Box>
                                    ) : (
                                        <>
                                            {/* Gráfico */}
                                            <Box
                                                sx={{
                                                    mx: 2.5,
                                                    p: 2,
                                                    borderRadius: 3,
                                                    textAlign: "center",
                                                    background: `linear-gradient(
                                                        135deg,
                                                        ${status.background},
                                                        #ffffff
                                                    )`
                                                }}
                                            >
                                                <TankGraphic
                                                    level={safeLevel}
                                                    capacity={safeCapacity}
                                                    unit={tank.unit}
                                                />
                                            </Box>

                                            {/* Barra de porcentaje */}
                                            <Box sx={{ px: 2.5, pt: 2 }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        mb: 0.8
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        fontWeight={700}
                                                    >
                                                        NIVEL ACTUAL
                                                    </Typography>

                                                    <Typography
                                                        variant="caption"
                                                        fontWeight={800}
                                                        color={status.color}
                                                    >
                                                        {percentage.toFixed(1)}%
                                                    </Typography>
                                                </Box>

                                                <LinearProgress
                                                    variant="determinate"
                                                    value={percentage}
                                                    sx={{
                                                        height: 8,
                                                        borderRadius: 10,
                                                        bgcolor: "#e2e8f0",
                                                        "& .MuiLinearProgress-bar": {
                                                            borderRadius: 10,
                                                            bgcolor: status.color
                                                        }
                                                    }}
                                                />
                                            </Box>

                                            {/* Información */}
                                            <Box
                                                sx={{
                                                    display: "grid",
                                                    gridTemplateColumns: "1fr 1fr",
                                                    gap: 1.5,
                                                    p: 2.5
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        p: 1.5,
                                                        borderRadius: 2.5,
                                                        bgcolor: "#f8fafc"
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        Capacidad
                                                    </Typography>

                                                    <Typography fontWeight={800} mt={0.3}>
                                                        {safeCapacity.toLocaleString()} {tank.unit}
                                                    </Typography>
                                                </Box>

                                                <Box
                                                    sx={{
                                                        p: 1.5,
                                                        borderRadius: 2.5,
                                                        bgcolor: "#f8fafc"
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        Disponible
                                                    </Typography>

                                                    <Typography fontWeight={800} mt={0.3}>
                                                        {available.toLocaleString()} {tank.unit}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </>
                                    )}

                                    {/* Acción */}
                                    <Box
                                        sx={{
                                            px: 2.5,
                                            pb: 2.5
                                        }}
                                    >
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            endIcon={<OpenInNewIcon />}
                                            onClick={() => openDetails(tank)}
                                            sx={{
                                                py: 1.1,
                                                borderRadius: 2.5,
                                                fontWeight: 700,
                                                textTransform: "none",
                                                boxShadow: "none",
                                                bgcolor: "#0f172a",
                                                "&:hover": {
                                                    bgcolor: "#1e293b",
                                                    boxShadow: "none"
                                                }
                                            }}
                                        >
                                            Ver movimientos
                                        </Button>
                                    </Box>
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>
            )}
        </Box>
    );
};

export default TankDashboard;